export type CandidateType = "HIGHCLASS" | "TALENT";

export type WorkHistory = {
  company: string;
  department: string;
  position: string;
  period: string;
  description: string;
};

export type Education = {
  school: string;
  faculty: string;
  degree: string;
  graduationYear: number;
};

export type LanguageSkill = {
  language: string;
  level: string;
};

export type Candidate = {
  id: string;
  memberId: string;
  memberType: CandidateType;
  isHot: boolean;
  lastName: string;
  firstName: string;
  age: number;
  gender: "男性" | "女性";
  prefecture: string;
  currentSalary: number;
  desiredSalary: number;
  currentCompany: string;
  currentDepartment: string;
  currentPosition: string;
  jobCategory: string;
  industry: string;
  education: Education;
  languages: LanguageSkill[];
  workHistories: WorkHistory[];
  desiredJobCategories: string[];
  desiredIndustries: string[];
  desiredLocations: string[];
  summary: string;
  resumeCharCount: number;
  managementExperience: number;
  employmentStatus: "現職" | "離職中";
  lastLoginDate: string;
  registeredCompanyCount: number;
};

export const mockCandidates: Candidate[] = [
  {
    id: "1",
    memberId: "BZ-2024-00001",
    memberType: "HIGHCLASS",
    isHot: true,
    lastName: "田中",
    firstName: "太郎",
    age: 38,
    gender: "男性",
    prefecture: "東京都",
    currentSalary: 1200,
    desiredSalary: 1400,
    currentCompany: "株式会社グローバルテック",
    currentDepartment: "プロダクト開発本部",
    currentPosition: "VP of Engineering",
    jobCategory: "ITエンジニア（システム開発・SE・インフラ）",
    industry: "IT・通信・インターネット",
    education: {
      school: "東京大学",
      faculty: "工学部 情報工学科",
      degree: "学士",
      graduationYear: 2010,
    },
    languages: [
      { language: "英語", level: "ビジネス会話レベル" },
      { language: "中国語", level: "日常会話レベル" },
    ],
    workHistories: [
      {
        company: "株式会社グローバルテック",
        department: "プロダクト開発本部",
        position: "VP of Engineering",
        period: "2020年4月 〜 現在",
        description:
          "エンジニア組織50名のマネジメント。マイクロサービスアーキテクチャへの移行をリード。年間開発生産性を40%向上。",
      },
      {
        company: "メガウェブ株式会社",
        department: "技術部",
        position: "テックリード",
        period: "2015年1月 〜 2020年3月",
        description:
          "大規模Webサービスのバックエンド開発リード。Go言語によるAPI基盤の設計・実装。チーム15名のマネジメント。",
      },
      {
        company: "株式会社スタートアップラボ",
        department: "開発部",
        position: "ソフトウェアエンジニア",
        period: "2010年4月 〜 2014年12月",
        description: "モバイルアプリケーションの企画・開発。iOS/Androidアプリを複数リリース。",
      },
    ],
    desiredJobCategories: ["CTO・VPoE", "プロダクトマネージャー"],
    desiredIndustries: ["IT・通信・インターネット", "SaaS"],
    desiredLocations: ["東京都", "リモート勤務可"],
    summary:
      "エンジニア組織のマネジメントとプロダクト開発の両面で15年以上の経験を持つ。マイクロサービス化、CI/CDパイプライン構築、アジャイル開発推進など、技術組織の変革をリードしてきた実績あり。直近ではVP of Engineeringとして50名規模のエンジニア組織を統括し、採用・育成・評価制度の設計まで担当。次のキャリアではCTOまたはVPoEとして、事業成長に直結する技術戦略の策定に携わりたい。",
    resumeCharCount: 2400,
    managementExperience: 50,
    employmentStatus: "現職",
    lastLoginDate: "2026-03-16",
    registeredCompanyCount: 3,
  },
  {
    id: "2",
    memberId: "BZ-2024-00002",
    memberType: "TALENT",
    isHot: false,
    lastName: "佐藤",
    firstName: "花子",
    age: 29,
    gender: "女性",
    prefecture: "大阪府",
    currentSalary: 550,
    desiredSalary: 700,
    currentCompany: "関西マーケティング株式会社",
    currentDepartment: "デジタルマーケティング部",
    currentPosition: "チームリーダー",
    jobCategory: "マーケティング・広報",
    industry: "広告・メディア",
    education: {
      school: "大阪大学",
      faculty: "経済学部 経営学科",
      degree: "学士",
      graduationYear: 2019,
    },
    languages: [{ language: "英語", level: "TOEIC 820点" }],
    workHistories: [
      {
        company: "関西マーケティング株式会社",
        department: "デジタルマーケティング部",
        position: "チームリーダー",
        period: "2022年7月 〜 現在",
        description:
          "デジタル広告運用チーム5名をマネジメント。Google/Meta広告の最適化により、CPA30%改善を実現。",
      },
      {
        company: "株式会社アドテクノロジー",
        department: "運用部",
        position: "広告運用担当",
        period: "2019年4月 〜 2022年6月",
        description:
          "リスティング広告・ディスプレイ広告の運用。月間予算5000万円規模のアカウント管理。",
      },
    ],
    desiredJobCategories: ["マーケティングマネージャー", "デジタルマーケティング"],
    desiredIndustries: ["IT・通信・インターネット", "EC・小売"],
    desiredLocations: ["大阪府", "東京都"],
    summary:
      "デジタルマーケティング領域で5年以上の経験。リスティング広告、SNS広告、SEO施策を横断的に担当。データドリブンなマーケティング戦略の立案・実行が強み。チームマネジメント経験あり。",
    resumeCharCount: 1800,
    managementExperience: 5,
    employmentStatus: "現職",
    lastLoginDate: "2026-03-15",
    registeredCompanyCount: 5,
  },
  {
    id: "3",
    memberId: "BZ-2024-00003",
    memberType: "HIGHCLASS",
    isHot: true,
    lastName: "鈴木",
    firstName: "健一",
    age: 45,
    gender: "男性",
    prefecture: "東京都",
    currentSalary: 1500,
    desiredSalary: 1800,
    currentCompany: "外資系戦略コンサルティングファーム",
    currentDepartment: "テクノロジー・デジタル部門",
    currentPosition: "パートナー",
    jobCategory: "経営企画・事業開発",
    industry: "コンサルティング",
    education: {
      school: "慶應義塾大学",
      faculty: "経済学部",
      degree: "学士",
      graduationYear: 2003,
    },
    languages: [
      { language: "英語", level: "ネイティブレベル" },
      { language: "フランス語", level: "日常会話レベル" },
    ],
    workHistories: [
      {
        company: "外資系戦略コンサルティングファーム",
        department: "テクノロジー・デジタル部門",
        position: "パートナー",
        period: "2018年1月 〜 現在",
        description:
          "DX戦略策定・実行支援を中心に、年間売上10億円以上のプロジェクトポートフォリオを統括。Fortune 500企業を複数担当。",
      },
      {
        company: "大手総合コンサルティングファーム",
        department: "戦略コンサルティング部門",
        position: "シニアマネージャー",
        period: "2010年4月 〜 2017年12月",
        description:
          "製造業・小売業向けのデジタル変革プロジェクトをリード。累計30件以上のプロジェクト完遂。",
      },
    ],
    desiredJobCategories: ["経営幹部（CEO/COO）", "事業開発責任者"],
    desiredIndustries: ["IT・通信・インターネット", "製造業"],
    desiredLocations: ["東京都"],
    summary:
      "戦略コンサルティング20年以上の経験を持つ。デジタルトランスフォーメーション、新規事業開発、M&A戦略を専門とし、グローバル企業のCxOレベルへのアドバイザリー実績が豊富。次のキャリアでは事業会社の経営幹部として、自らの手で事業を創り・伸ばすことに挑戦したい。",
    resumeCharCount: 3200,
    managementExperience: 100,
    employmentStatus: "現職",
    lastLoginDate: "2026-03-14",
    registeredCompanyCount: 2,
  },
  {
    id: "4",
    memberId: "BZ-2024-00004",
    memberType: "TALENT",
    isHot: true,
    lastName: "高橋",
    firstName: "美咲",
    age: 26,
    gender: "女性",
    prefecture: "福岡県",
    currentSalary: 420,
    desiredSalary: 550,
    currentCompany: "株式会社九州システムズ",
    currentDepartment: "開発部",
    currentPosition: "ソフトウェアエンジニア",
    jobCategory: "ITエンジニア（システム開発・SE・インフラ）",
    industry: "IT・通信・インターネット",
    education: {
      school: "九州大学",
      faculty: "理学部 情報科学科",
      degree: "学士",
      graduationYear: 2022,
    },
    languages: [{ language: "英語", level: "日常会話レベル" }],
    workHistories: [
      {
        company: "株式会社九州システムズ",
        department: "開発部",
        position: "ソフトウェアエンジニア",
        period: "2022年4月 〜 現在",
        description:
          "React/TypeScriptを使用したフロントエンド開発。GraphQL APIの設計・実装。社内ハッカソンで最優秀賞受賞。",
      },
    ],
    desiredJobCategories: ["フロントエンドエンジニア", "フルスタックエンジニア"],
    desiredIndustries: ["IT・通信・インターネット", "SaaS"],
    desiredLocations: ["福岡県", "東京都", "リモート勤務可"],
    summary:
      "React/TypeScript/GraphQLを中心としたモダンフロントエンド開発が得意。入社2年目で社内ハッカソン最優秀賞を受賞。技術ブログの執筆やOSS貢献にも積極的。フルスタックへのキャリア拡大を志向。",
    resumeCharCount: 1200,
    managementExperience: 0,
    employmentStatus: "現職",
    lastLoginDate: "2026-03-17",
    registeredCompanyCount: 8,
  },
  {
    id: "5",
    memberId: "BZ-2024-00005",
    memberType: "HIGHCLASS",
    isHot: false,
    lastName: "渡辺",
    firstName: "誠",
    age: 52,
    gender: "男性",
    prefecture: "東京都",
    currentSalary: 1100,
    desiredSalary: 1200,
    currentCompany: "大手金融グループ",
    currentDepartment: "リスク管理部",
    currentPosition: "部長",
    jobCategory: "金融・ファイナンス",
    industry: "金融・保険",
    education: {
      school: "一橋大学",
      faculty: "商学部",
      degree: "修士",
      graduationYear: 1998,
    },
    languages: [{ language: "英語", level: "ビジネス会話レベル" }],
    workHistories: [
      {
        company: "大手金融グループ",
        department: "リスク管理部",
        position: "部長",
        period: "2019年4月 〜 現在",
        description:
          "グループ全体のリスク管理体制の統括。Basel III対応、ストレステスト設計、リスク量計測モデルの高度化を推進。",
      },
      {
        company: "外資系投資銀行",
        department: "リスク管理部門",
        position: "ディレクター",
        period: "2008年1月 〜 2019年3月",
        description:
          "市場リスク・信用リスクの定量分析。リーマンショック後の規制対応プロジェクトをリード。",
      },
    ],
    desiredJobCategories: ["リスク管理責任者（CRO）", "CFO"],
    desiredIndustries: ["金融・保険", "フィンテック"],
    desiredLocations: ["東京都"],
    summary:
      "金融業界で25年以上の経験。リスク管理、コンプライアンス、内部統制の専門家。Basel規制対応、ストレステスト、ALM管理など幅広い領域をカバー。フィンテック企業でのCRO/CFOポジションに関心。",
    resumeCharCount: 2800,
    managementExperience: 30,
    employmentStatus: "現職",
    lastLoginDate: "2026-03-10",
    registeredCompanyCount: 2,
  },
  {
    id: "6",
    memberId: "BZ-2024-00006",
    memberType: "TALENT",
    isHot: false,
    lastName: "伊藤",
    firstName: "さくら",
    age: 31,
    gender: "女性",
    prefecture: "愛知県",
    currentSalary: 650,
    desiredSalary: 800,
    currentCompany: "トヨタ系部品メーカー",
    currentDepartment: "生産技術部",
    currentPosition: "主任",
    jobCategory: "生産技術・製造・品質管理",
    industry: "メーカー（自動車・輸送機器）",
    education: {
      school: "名古屋大学",
      faculty: "工学部 機械工学科",
      degree: "修士",
      graduationYear: 2017,
    },
    languages: [{ language: "英語", level: "TOEIC 750点" }],
    workHistories: [
      {
        company: "トヨタ系部品メーカー",
        department: "生産技術部",
        position: "主任",
        period: "2020年4月 〜 現在",
        description:
          "EV向け新製品の量産ライン立ち上げ。IoTセンサーによる品質管理システムを導入し、不良率0.01%以下を達成。",
      },
      {
        company: "トヨタ系部品メーカー",
        department: "設計部",
        position: "設計エンジニア",
        period: "2017年4月 〜 2020年3月",
        description: "自動車用電装品の3D CAD設計。CATIA V5を使用した製品設計と強度解析。",
      },
    ],
    desiredJobCategories: ["生産技術マネージャー", "製造DX推進"],
    desiredIndustries: ["メーカー（自動車・輸送機器）", "メーカー（電機・電子）"],
    desiredLocations: ["愛知県", "東京都"],
    summary:
      "自動車部品メーカーで生産技術・設計の両方を経験。EV関連の量産ライン立ち上げやIoT活用による品質改善の実績あり。製造業のDX推進に関心が高く、スマートファクトリー構想の実現に貢献したい。",
    resumeCharCount: 1600,
    managementExperience: 8,
    employmentStatus: "現職",
    lastLoginDate: "2026-03-12",
    registeredCompanyCount: 4,
  },
  {
    id: "7",
    memberId: "BZ-2024-00007",
    memberType: "HIGHCLASS",
    isHot: true,
    lastName: "山本",
    firstName: "龍一",
    age: 42,
    gender: "男性",
    prefecture: "東京都",
    currentSalary: 1350,
    desiredSalary: 1500,
    currentCompany: "グローバルEC企業",
    currentDepartment: "データサイエンス部",
    currentPosition: "部門長",
    jobCategory: "データサイエンティスト・AI",
    industry: "IT・通信・インターネット",
    education: {
      school: "東京工業大学",
      faculty: "情報理工学院",
      degree: "博士",
      graduationYear: 2008,
    },
    languages: [
      { language: "英語", level: "ネイティブレベル" },
      { language: "ドイツ語", level: "日常会話レベル" },
    ],
    workHistories: [
      {
        company: "グローバルEC企業",
        department: "データサイエンス部",
        position: "部門長",
        period: "2021年1月 〜 現在",
        description:
          "データサイエンティスト30名の組織を統括。レコメンデーションエンジンの刷新により、CVR15%向上を達成。LLM活用の社内検索システムをPoCから本番導入。",
      },
      {
        company: "米国テック企業（日本法人）",
        department: "Applied ML Team",
        position: "Senior ML Engineer",
        period: "2013年6月 〜 2020年12月",
        description:
          "自然言語処理・画像認識の研究開発。国際学会（NeurIPS、ICML）での論文発表実績あり。",
      },
    ],
    desiredJobCategories: ["CDO（Chief Data Officer）", "AI/ML部門責任者"],
    desiredIndustries: ["IT・通信・インターネット", "金融・保険"],
    desiredLocations: ["東京都", "リモート勤務可"],
    summary:
      "機械学習・データサイエンス領域で15年以上の経験。博士号取得後、米国テック企業と日本EC企業でMLエンジニアリングの最前線に従事。NeurIPS等の国際学会での論文発表実績、レコメンデーション・NLP・画像認識の幅広い知見を持つ。次はCDOとしてデータドリブン経営の推進を担いたい。",
    resumeCharCount: 3500,
    managementExperience: 30,
    employmentStatus: "現職",
    lastLoginDate: "2026-03-16",
    registeredCompanyCount: 1,
  },
  {
    id: "8",
    memberId: "BZ-2024-00008",
    memberType: "TALENT",
    isHot: false,
    lastName: "中村",
    firstName: "優子",
    age: 34,
    gender: "女性",
    prefecture: "神奈川県",
    currentSalary: 750,
    desiredSalary: 900,
    currentCompany: "外資系人材サービス会社",
    currentDepartment: "人事部",
    currentPosition: "HRビジネスパートナー",
    jobCategory: "人事・総務・法務",
    industry: "人材サービス",
    education: {
      school: "早稲田大学",
      faculty: "法学部",
      degree: "学士",
      graduationYear: 2014,
    },
    languages: [{ language: "英語", level: "ビジネス会話レベル" }],
    workHistories: [
      {
        company: "外資系人材サービス会社",
        department: "人事部",
        position: "HRビジネスパートナー",
        period: "2021年1月 〜 現在",
        description:
          "事業部門500名のHRBP。組織設計、人材配置、エンゲージメント施策を推進。離職率を前年比5ポイント改善。",
      },
      {
        company: "大手IT企業",
        department: "人事企画部",
        position: "人事企画担当",
        period: "2014年4月 〜 2020年12月",
        description: "人事制度設計（等級・評価・報酬）、新卒採用、研修企画を幅広く担当。",
      },
    ],
    desiredJobCategories: ["CHRO", "人事部長"],
    desiredIndustries: ["IT・通信・インターネット", "SaaS"],
    desiredLocations: ["東京都", "神奈川県"],
    summary:
      "人事領域で10年以上の経験。制度設計、採用、HRBP、組織開発を一通り経験。外資系でのグローバルHR運用経験とデータドリブンな人事施策が強み。テック企業のCHROを目指している。",
    resumeCharCount: 2000,
    managementExperience: 10,
    employmentStatus: "現職",
    lastLoginDate: "2026-03-13",
    registeredCompanyCount: 6,
  },
  {
    id: "9",
    memberId: "BZ-2024-00009",
    memberType: "TALENT",
    isHot: false,
    lastName: "小林",
    firstName: "翔太",
    age: 27,
    gender: "男性",
    prefecture: "東京都",
    currentSalary: 480,
    desiredSalary: 600,
    currentCompany: "SaaSスタートアップ",
    currentDepartment: "カスタマーサクセス部",
    currentPosition: "CS担当",
    jobCategory: "営業・カスタマーサクセス",
    industry: "IT・通信・インターネット",
    education: {
      school: "明治大学",
      faculty: "商学部",
      degree: "学士",
      graduationYear: 2021,
    },
    languages: [{ language: "英語", level: "日常会話レベル" }],
    workHistories: [
      {
        company: "SaaSスタートアップ",
        department: "カスタマーサクセス部",
        position: "CS担当",
        period: "2023年1月 〜 現在",
        description:
          "エンタープライズ顧客20社を担当。解約率を2%以下に維持。アップセル提案により、担当顧客のARR30%増加。",
      },
      {
        company: "大手通信キャリア",
        department: "法人営業部",
        position: "法人営業",
        period: "2021年4月 〜 2022年12月",
        description:
          "中堅企業向けのネットワーク・クラウドソリューション提案営業。年間目標達成率120%。",
      },
    ],
    desiredJobCategories: ["カスタマーサクセスマネージャー", "事業開発"],
    desiredIndustries: ["IT・通信・インターネット", "SaaS"],
    desiredLocations: ["東京都"],
    summary:
      "SaaS業界でのカスタマーサクセスと法人営業の経験を持つ。データ分析に基づくチャーン予防とアップセル戦略が強み。顧客の課題を深く理解し、プロダクト改善にフィードバックするブリッジ役として活躍中。",
    resumeCharCount: 1400,
    managementExperience: 0,
    employmentStatus: "現職",
    lastLoginDate: "2026-03-17",
    registeredCompanyCount: 10,
  },
  {
    id: "10",
    memberId: "BZ-2024-00010",
    memberType: "HIGHCLASS",
    isHot: false,
    lastName: "松田",
    firstName: "雅人",
    age: 48,
    gender: "男性",
    prefecture: "東京都",
    currentSalary: 980,
    desiredSalary: 1100,
    currentCompany: "",
    currentDepartment: "",
    currentPosition: "",
    jobCategory: "経営企画・事業開発",
    industry: "不動産・建設",
    education: {
      school: "京都大学",
      faculty: "法学部",
      degree: "学士",
      graduationYear: 2000,
    },
    languages: [{ language: "英語", level: "ビジネス会話レベル" }],
    workHistories: [
      {
        company: "大手デベロッパー",
        department: "経営企画部",
        position: "執行役員 経営企画部長",
        period: "2018年4月 〜 2025年12月",
        description:
          "中期経営計画の策定・推進。PropTech領域への新規投資を統括し、3年間で投資回収を達成。海外不動産ファンドとのJV設立を主導。",
      },
      {
        company: "総合商社",
        department: "不動産本部",
        position: "部長",
        period: "2008年4月 〜 2018年3月",
        description:
          "国内外の不動産投資・開発プロジェクトを担当。累計投資額2000億円規模の案件を管理。",
      },
    ],
    desiredJobCategories: ["経営幹部（CEO/COO）", "事業開発責任者"],
    desiredIndustries: ["不動産・建設", "IT・通信・インターネット"],
    desiredLocations: ["東京都"],
    summary:
      "不動産・総合商社で25年以上の経験。経営企画、事業開発、海外投資、JV組成が専門。PropTech領域への知見を活かし、テクノロジーと不動産の融合に取り組みたい。現在離職中のため、即日入社可能。",
    resumeCharCount: 2600,
    managementExperience: 40,
    employmentStatus: "離職中",
    lastLoginDate: "2026-03-11",
    registeredCompanyCount: 3,
  },
];
